import { useState, MouseEvent, ReactNode } from 'react';
import { Menu, MenuItemType, type IMenuItem } from '@components/menu/Menu';
import { IconButton } from '@components/iconButton/IconButton';
import { EllipsisH } from '@assets/svgs';
import type { PopoverOrigin } from '@mui/material';

export interface MenuWrapperProps {
  items: IMenuItem[];
  /** Called when an item (without its own handleOnClick) is clicked */
  onClickMenuItem?: (index: number, text?: string) => void;
  /** Optional custom trigger. Receives open state. If provided, you must call onOpen from its onClick */
  triggerRender?: (opts: { open: boolean; onOpen: (e: MouseEvent<HTMLElement>) => void }) => ReactNode;
  /** Override anchor origin */
  anchorOrigin?: PopoverOrigin;
  /** Override transform origin */
  transformOrigin?: PopoverOrigin;
  /** Size and variant passthrough for default trigger */
  iconButtonProps?: Omit<React.ComponentProps<typeof IconButton>, 'onClick'>;
}

/**
 * Reusable wrapper that encapsulates the common pattern of an Ellipsis IconButton
 * controlling a Menu. Accepts item definitions identical to existing Menu usage.
 */
export const MenuWrapper = ({
  items,
  onClickMenuItem,
  triggerRender,
  anchorOrigin = { vertical: 'top', horizontal: 'right' },
  transformOrigin = { vertical: 'top', horizontal: 'right' },
  iconButtonProps,
}: MenuWrapperProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      {triggerRender ? (
        triggerRender({ open, onOpen: handleOpen })
      ) : (
        <IconButton size='small' variant='rounded' aria-label='Open menu' {...iconButtonProps} onClick={handleOpen}>
          <EllipsisH fill='secondary.main' />
        </IconButton>
      )}
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        items={items?.map((item) => ({
          ...item,
          handleOnClick: item.handleOnClick
            ? () => {
                handleClose();
                item.handleOnClick?.();
              }
            : () => {
                handleClose();
              },
        }))}
        onClickMenuItem={onClickMenuItem ? (index, text) => onClickMenuItem(index, text) : () => {}}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      />
    </>
  );
};

export { MenuItemType };
